import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../lib/api";
import { PageTitle } from "../../components/Layout";
import { Modal, Field } from "../../components/ui-modal";
import { toast } from "sonner";
import * as Icons from "lucide-react";
import MetiersInput from "../../components/MetiersInput";
import { CardsSkeleton } from "../../components/Feedback";

const RATING_OPTIONS = [
  { value: "", label: "Toutes les notes" },
  { value: "4", label: "≥ 4 ★" },
  { value: "3", label: "≥ 3 ★" },
  { value: "2", label: "≥ 2 ★" },
  { value: "unrated", label: "Non évalués" },
];

// Valeurs distinctes d'un champ, triées en français — hors du composant pour ne pas changer d'identité à chaque rendu.
const uniqBy = (list, key) => [...new Set(list.map((c) => (c[key] || "").trim()).filter(Boolean))].sort((a, b) => a.localeCompare(b, "fr"));

export default function AdminCollaborators() {
  // null = pas encore chargé (squelette) ; [] = chargé et vide. Avant, l'état vide
  // « Aucun collaborateur » et « 0 / 0 » s'affichaient pendant le chargement (dette du 18/08).
  const [loaded, setLoaded] = useState(null);
  const collabs = useMemo(() => loaded || [], [loaded]);
  const [newCollab, setNewCollab] = useState(false);
  const [cf, setCf] = useState({ name: "", email: "", password: "", job_roles: [] });
  const [search, setSearch] = useState("");
  const [fRating, setFRating] = useState("");
  const [fCity, setFCity] = useState("");
  const [fCountry, setFCountry] = useState("");
  const [fJob, setFJob] = useState("");
  const nav = useNavigate();

  const load = () => {
    api.get("/admin/collaborators").then((r) => setLoaded(r.data)).catch(() => { setLoaded([]); toast.error("Impossible de charger les collaborateurs"); });
  };
  useEffect(() => { load(); }, []);

  const createCollab = async (e) => { e.preventDefault(); if (!cf.job_roles.length) return toast.error("Ajoutez au moins un métier"); try { await api.post("/admin/collaborators", cf); toast.success("Collaborateur créé"); setNewCollab(false); setCf({ name: "", email: "", password: "", job_roles: [] }); load(); } catch (e2) { toast.error(e2.response?.data?.detail || "Erreur"); } };

  const cities = useMemo(() => uniqBy(collabs, "city"), [collabs]);
  const countries = useMemo(() => uniqBy(collabs, "country"), [collabs]);
  const jobs = useMemo(() => [...new Set(collabs.flatMap((c) => (c.job_roles && c.job_roles.length ? c.job_roles : (c.job_role ? [c.job_role] : []))).map((j) => j.trim()).filter(Boolean))].sort((a, b) => a.localeCompare(b, "fr")), [collabs]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return collabs.filter((c) => {
      const roles = (c.job_roles && c.job_roles.length ? c.job_roles : (c.job_role ? [c.job_role] : []));
      if (q) {
        const hay = [c.name, c.email, c.city, c.country, ...roles].map((x) => (x || "").toLowerCase()).join(" ");
        if (!hay.includes(q)) return false;
      }
      if (fCity && (c.city || "").trim() !== fCity) return false;
      if (fCountry && (c.country || "").trim() !== fCountry) return false;
      if (fJob && !roles.some((r) => r.trim() === fJob)) return false;
      if (fRating === "unrated") { if (c.avg_rating != null) return false; }
      else if (fRating) { if (c.avg_rating == null || c.avg_rating < parseFloat(fRating)) return false; }
      return true;
    });
  }, [collabs, search, fRating, fCity, fCountry, fJob]);

  const activeFilters = fRating || fCity || fCountry || fJob || search;
  const resetFilters = () => { setSearch(""); setFRating(""); setFCity(""); setFCountry(""); setFJob(""); };

  const selCls = "bg-white border border-marine/15 rounded-sm px-3 py-2 text-sm text-marine focus:border-marine/50 outline-none";

  return (
    <div>
      <PageTitle overline="Équipe" title="Collaborateurs" action={
        <button onClick={() => setNewCollab(true)} data-testid="new-collab-btn" className="bg-marine text-white rounded-sm px-4 py-2.5 text-sm hover:bg-marine-hover transition-colors">+ Collaborateur</button>
      } />

      <div className="bg-sable rounded-sm border border-marine/10 p-4 mb-4" data-testid="collab-filters">
        <div className="relative mb-3">
          <Icons.Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-marine" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} data-testid="collab-search"
            placeholder="Rechercher par nom, e-mail, ville, pays, métier..."
            className="w-full bg-creme border border-marine/15 rounded-sm pl-9 pr-3 py-2.5 text-sm text-marine focus:border-marine/50 outline-none" />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select aria-label="Filtrer par note" value={fRating} onChange={(e) => setFRating(e.target.value)} data-testid="filter-rating" className={selCls}>
            {RATING_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <select aria-label="Filtrer par ville" value={fCity} onChange={(e) => setFCity(e.target.value)} data-testid="filter-city" className={selCls}>
            <option value="">Toutes les villes</option>
            {cities.map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
          <select aria-label="Filtrer par pays" value={fCountry} onChange={(e) => setFCountry(e.target.value)} data-testid="filter-country" className={selCls}>
            <option value="">Tous les pays</option>
            {countries.map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
          <select aria-label="Filtrer par métier" value={fJob} onChange={(e) => setFJob(e.target.value)} data-testid="filter-job" className={selCls}>
            <option value="">Tous les métiers</option>
            {jobs.map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
          {activeFilters ? (
            <button onClick={resetFilters} data-testid="filter-reset" className="text-xs px-3 py-2 text-rouge hover:underline inline-flex items-center gap-1">
              <Icons.X size={16} /> Réinitialiser
            </button>
          ) : null}
          <span className="ml-auto text-xs text-marine/80" data-testid="collab-count">{filtered.length} / {collabs.length} collaborateur(s)</span>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3" data-testid="collab-list">
        {filtered.map((c) => (
          <button key={c.id} onClick={() => nav(`/admin/collaborateurs/${c.id}`)} data-testid={`collab-card-${c.id}`}
            className="text-left bg-sable rounded-sm border border-marine/10 p-4 hover:border-marine/40 flex flex-wrap items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="font-display text-lg text-marine">{c.name}</div>
              <div className="text-sm text-marine/80">{c.job_role} · {c.email}</div>
              {(c.city || c.country) && (
                <div className="text-xs text-marine/80 mt-0.5 inline-flex items-center gap-1"><Icons.MapPin size={16} className="text-marine" /> {[c.city, c.country].filter(Boolean).join(", ")}</div>
              )}
            </div>
            <div className="flex items-center gap-3">
              {c.late_payments > 0 && (
                <span className="text-[9px] px-2.5 py-1 rounded-sm bg-rouge-50 text-rouge border border-rouge/30 inline-flex items-center gap-1" data-testid={`collab-late-${c.id}`} title={`${c.late_payments} paiement(s) en retard`}>{c.late_payments} en retard</span>
              )}
              {c.avg_rating != null
                ? <span className="text-[9px] px-2.5 py-1 rounded-sm bg-ambre-50 text-ambre-texte border border-ambre/30 inline-flex items-center gap-1" data-testid={`collab-rating-${c.id}`} title={`${c.eval_count} mission(s) évaluée(s)`}>{c.avg_rating}/5 <span className="text-ambre-texte">({c.eval_count})</span></span>
                : <span className="text-[9px] px-2.5 py-1 rounded-sm bg-sable-clair text-gris-texte border border-gris-texte/30 inline-flex items-center gap-1" data-testid={`collab-rating-${c.id}`}>Non évalué</span>}
              <span className="text-[9px] px-2.5 py-1 rounded-sm bg-sable border border-marine/30 text-marine">{c.active_missions || 0} mission(s) en cours</span>
              <Icons.ChevronRight size={16} className="text-marine" />
            </div>
          </button>
        ))}
        {loaded === null && <CardsSkeleton count={3} cols={1} />}
        {loaded !== null && collabs.length === 0 && <p className="text-sm text-marine/80">Aucun collaborateur.</p>}
        {collabs.length > 0 && filtered.length === 0 && <p className="text-sm text-marine/80" data-testid="collab-empty-filter">Aucun collaborateur ne correspond aux filtres.</p>}
      </div>

      {newCollab && (
        <Modal onClose={() => setNewCollab(false)} title="Nouveau collaborateur">
          <form onSubmit={createCollab} className="space-y-3">
            <Field label="Nom"><input required value={cf.name} onChange={(e) => setCf({ ...cf, name: e.target.value })} data-testid="collab-name" className="inp" /></Field>
            <Field label="Métiers"><MetiersInput testid="collab-metiers" value={cf.job_roles} onChange={(v) => setCf({ ...cf, job_roles: v })} /></Field>
            <Field label="Email"><input required type="email" value={cf.email} onChange={(e) => setCf({ ...cf, email: e.target.value })} data-testid="collab-email" className="inp" /></Field>
            <Field label="Mot de passe"><input required value={cf.password} onChange={(e) => setCf({ ...cf, password: e.target.value })} data-testid="collab-password" className="inp" /></Field>
            <div className="flex justify-end gap-2 pt-2"><button type="button" onClick={() => setNewCollab(false)} className="px-4 py-2 text-sm text-marine/80">Annuler</button><button data-testid="collab-submit" className="bg-marine text-white rounded-sm px-5 py-2 text-sm">Créer</button></div>
          </form>
        </Modal>
      )}
    </div>
  );
}
